import api from "@/api/url";
import { ALLOWED_COUNTRIES, COUNTRY_RESTRICTION_ENCODING } from "@/utils/enums";

export default {
    namespaced: true,

    state: () => {
        return {
            currentRoute: {
                main: "",
                sub: "",
            },
            paths: [],
            completionStatus: "",
            completedProjectCount: 0,
            pdfFile: null,
            activities: {
                list: [],
                pageData: {
                    currentPage: 1,
                    totalPages: 0,
                    pageSize: 20,
                    total: 0,
                },
            },
            pageWrapperLoading: false,
            isNewUser: false,
            showRankModal: false,
            confetti: {
                show: false,
                type: "", // "onboarding" | "rank"
            },
            calendarRefreshList: false,
            checkUpgradeLoading: false,
            checkUpgradeFields: {
                invoices: false,
                clients: false,
                projects: false,
                rateCards: false,
                calendar: false,
                integrations: false,
            },
            showUpgradeModal: false,
            unReadActivityCount: 0,
            landingPageSectionId: "pricing",
            selectedPlan: "free",
            initiatePaidPlan: false,
            publicProfile: null,
            publicProfilePage: null,
            ipAddress: "",
            checks: [],
        };
    },

    getters: {
        currentRoute: state => state.currentRoute,
        ipAddress: state => state.ipAddress,
        paths: state => state.paths,
        completionStatus: state => state.completionStatus,
        completedProjectCount: state => state.completedProjectCount,
        pdfFile: state => state.pdfFile,
        notifications: state => state.notifications,
        activities: state => state.activities,
        pageWrapperLoading: state => state.pageWrapperLoading,
        isNewUser: state => state.isNewUser,
        showRankModal: state => state.showRankModal,
        confetti: state => state.confetti,
        calendarRefreshList: state => state.calendarRefreshList,
        checkUpgradeLoading: state => state.checkUpgradeLoading,
        checkUpgradeFields: state => state.checkUpgradeFields,
        showUpgradeModal: state => state.showUpgradeModal,
        unReadActivityCount: state => state.unReadActivityCount,
        landingPageSectionId: state => state.landingPageSectionId,
        selectedPlan: state => state.selectedPlan,
        initiatePaidPlan:  state => state.initiatePaidPlan,
        publicProfile: state => state.publicProfile,
        checks: state => state.checks,
    },

    mutations: {
        set(state, [key, value]) {
            state[key] = value;
        },
    },
    
    actions: {
        setLandingPageSectionId({ commit }, id) {
            commit("set", ["landingPageSectionId", id]);
        },

        setSelectedPlan({ commit }, val) {
            commit("set", ["selectedPlan", val]);
        },
        
        setInitiatePaidPlan({ commit }, bool) {
            commit("set", ["initiatePaidPlan", bool]);
        },

        async checkUpgradeStatus({ state, commit }, type) {            
            try {
                commit("set", ["showUpgradeModal", false]);
                commit("set", ["checkUpgradeLoading", true]);
                commit("set", ["checkUpgradeFields", {
                    ...state.checkUpgradeFields,
                    [`${type}`]: true,
                }]);

                await api.app.checkUpgradeStatus({ type });

                commit("set", ["checkUpgradeLoading", false]);
                commit("set", ["checkUpgradeFields", {
                    ...state.checkUpgradeFields,
                    [`${type}`]: false,
                }]);
            } catch (error) {
                commit("set", ["checkUpgradeLoading", false]);
                const upgradeErrorMsg = error?.response?.data?.message;

                if (typeof upgradeErrorMsg === "string" && upgradeErrorMsg.includes("upgrade")) {
                    commit("set", ["checkUpgradeFields", {
                        ...state.checkUpgradeFields,
                        [`${type}`]: false,
                    }]);
                    commit("set", ["showUpgradeModal", true]);
                }             
                throw error;
            }
        },

        closeUpgradeModal({ commit, state }, type) {
            commit("set", ["checkUpgradeFields", {
                ...state.checkUpgradeFields,
                [`${type}`]: false,
            }]);         
            commit("set", ["showUpgradeModal", false]);
        },
        
        setCurrentRouteTwo({ commit, state }, routePath) {
            const paths = typeof routePath === "string" && routePath.split("/");
            const modifiedPaths = [];

            for (let i = 0; i < paths.length; i++) {
                if (paths[i] !== "" && paths[i] !== "dashboard") {
                    if (paths[i].includes("and")) {
                        const replacedPath = paths[i].replace("-and-", " & ");
                        modifiedPaths.push(replacedPath);
                    } else if (paths[i].includes("-")) {
                        const replacedPath = paths[i].replace("-", " ");
                        modifiedPaths.push(replacedPath);
                    } else {
                        modifiedPaths.push(paths[i]);
                    }
                }
            }

            commit("set", ["paths", modifiedPaths]);
            commit("set", ["currentRoute", { ...state.currentRoute, main: modifiedPaths[0] }]);
        },

        fetchCompletedProjectCount({ commit, state }, { refresh = false, manualUpdate = false }) {            
            if (state.completedProjectCount && !refresh) {
                return;
            }

            if (manualUpdate) {
                commit("set", ["completedProjectCount", state.completedProjectCount + 1]);
                return;
            }

            return api.app.getProjectCount()
                .then((resp) => {
                    const count = resp?.data.data.count;
                    commit("set", ["completedProjectCount", count]);
                });
        }, 

        togglePageLoading({ commit }, bool) {
            commit("set", ["pageWrapperLoading", bool]);
        },
        
        toggleNewUser({ commit }, bool) {
            commit("set", ["isNewUser", bool]);
        },

        toggleRank({ commit }, bool) {
            commit("set", ["showRankModal", bool]);
        }, 

        toggleConfetti({ commit }, payload) {
            commit("set", ["confetti", payload]);
        },

        savePublicProfileUpdates({ commit }, payload) {            
            return api.user.savePublicProfileUpdates(payload)
                .then((resp) => {
                    commit("set", ["publicProfile", resp?.data?.data]);
                });
        },

        fetchPublicProfile({ commit }) {            
            return api.user.fetchPublicProfile()
                .then((resp) => {       
                    commit("set", ["publicProfile", resp?.data?.data]);
                });
        },
        
        // USER ACTIVITIES --------------------------------------
        setUnreadActivityCount({ commit }, payload) {            
            commit("set", ["unReadActivityCount", payload.count]);
        },

        fetchActivities({ commit, state }, { refresh, params }) {
            if (state.activities?.length && !refresh) {
                return state.activities;
            }            

            return api.app.getActivityLogs(params)
                .then((res) => {
                    const data = res.data.data;

                    commit("set", ["activities", {
                        ...state.activities,
                        list: data.data,
                        pageData: {
                            ...state.activities.pageData,
                            ...data.pageDetails,
                        },
                    }]);
                    commit("set", ["unReadActivityCount", data?.count]);
                });
        },

        addActivity({ commit, state }, payload) {           
            commit("set", ["activities", {
                ...state.activities,
                list: [payload, ...state.activities.list],
                pageData: {
                    ...state.activities.pageData,
                    total: state.activities.pageData.total + 1,
                    totalPages: Math.ceil((state.activities.pageData.total + 1) / state.activities.pageData.pageSize),
                },
            }]);
        },

        markOneRead({ state, commit }, id) {            
            return api.app.markOne(id)
                .then((resp) => {
                    const updatedLog = state.activities.list.filter(x => x.id !== id);

                    commit("set", ["activities", {
                        ...state.activities,
                        list: updatedLog,
                        pageData: {
                            ...state.activities.pageData,
                            total: state.activities.pageData.total - 1,
                            totalPages: Math.ceil((state.activities.pageData.total - 1) / state.activities.pageData.pageSize),
                        },
                    }]);

                    // fetch activity count
                    commit("set", ["unReadActivityCount", state.unReadActivityCount - 1]);
                });
        },

        markAllRead({ commit, state }, ids) {            
            return api.app.markAll(ids)
                .then((resp) => {
                    commit("set", ["activities", {
                        ...state.activities,
                        list: [],
                        pageData: {
                            ...state.activities.pageData,
                            total: 0,
                            totalPages: 0,
                        },
                    }]);
                    commit("set", ["unReadActivityCount", 0]);
                });
        },
        
        reportBugs(_, payload) {      
            return api.app.reportBugs(payload)
                .then((resp) => {
                    return resp;
                });
        },

        checkIpAddress({ commit }) {            
            return api.app.fetchUnreadCount()
                .then((resp) => {
                    const count = resp?.data.data.count;
                    commit("set", ["unReadActivityCount", count]);
                });
        },

        getOnboardingChecks({ commit }) {
            return api.user.onboardingChecks()
                .then((resp) => {                    
                    commit("set", ["checks", resp?.data?.data]);
                }); 
        },
    },
};